import { useApiWithAuth } from '@/hooks/api';
import { useAuth } from '@/hooks/auth';
import { useGroupsStore } from '../groups';
import { useEffect, useMemo, useRef, useState } from 'react';
import { loadGroups } from '../groups/actions';
import type { Person } from './types';
import type { GroupTypeKey } from '@/shared/types/group';
import { asMultiValue, asSingleValue } from '@/lib/arrays';
import { useDebounce } from '@/hooks/debounce';
import { makeSearchParam } from '@/components/people/people/utils';
import { loadMemberStatus } from '../memberstatus/actions';
import { useMemberStatusStore } from '../memberstatus';
import { apiInvoke } from '../helpers/load';
import type { PersonDocument } from '@/shared/types/pco/person';
import { personDocumentToStorePerson } from './mapFromMongo';
import type { ApiResponse } from '@/shared/types/api';
import {
  normalizePcoGroupLeaderRows,
  type PcoGroupAccessShape,
} from '@/shared/types/pco/group';
import type { PcoServiceTeamAccessShape } from '@/shared/types/pco/service';

export function useMemberStatus() {
  const store = useMemberStatusStore();
  const { api, auth } = useApiWithAuth();
  const loading = useRef(false);
  const loaded = useRef(0);
  const maxAgeSeconds = 900;

  useEffect(() => {
    const now = Date.now();
    if (loading.current || loaded.current > now - maxAgeSeconds * 1000) {
      return;
    }

    loading.current = true;

    loadMemberStatus(store, { force: false, api, auth })
      .then(() => (loading.current = false))
      .catch((err) => {
        loading.current = false;
        loaded.current = now;
        console.error(err);
      });
  }, [store, auth, api]);

  return {
    lookup: store.state.lookup,
    list: store.state.list,
  };
}

export function useGroups(
  campus?: string | string[],
  type?: GroupTypeKey,
  parent?: string,
) {
  const store = useGroupsStore();
  const { api, auth } = useApiWithAuth();
  useEffect(() => {
    loadGroups(store, {
      force: false,
      api,
      auth,
      campus: asSingleValue(campus),
      campuses: asMultiValue(campus),
    });
  }, [store, auth, api, campus]);
  const list = useMemo(() => {
    let list = store.computed.activeGroupsList;
    if (campus) {
      const campuses = asMultiValue(campus, false);
      list = list.filter((item) => campuses?.includes(item.data.campus_id));
    }

    if (type) {
      list = list.filter((item) => item.data.type === type);
    }

    if (parent) {
      list = list.filter((item) => item.data.parent_id === parent);
    }

    return list;
  }, [store.computed.activeGroupsList, campus, parent, type]);
  const lookup = store.computed.groupLookup;

  return useMemo(
    () => ({
      list,
      lookup,
      // TODO parent ids and level
    }),
    [list, lookup],
  );
}

export function usePeopleLookup({
  search,
  limit = 20,
  debounce = 400,
}: {
  search: string;
  limit?: number;
  debounce?: number;
}) {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const input = useDebounce(search, debounce);
  const last = useRef('');
  const { api, auth } = useApiWithAuth();
  const apiRef = useRef(api);
  const authRef = useRef(auth);
  apiRef.current = api;
  authRef.current = auth;

  useEffect(() => {
    if (last.current === input) {
      return () => {};
    }

    last.current = input;
    if (!input.trim()) {
      setPeople([]);
      setEmpty(false);
      last.current = '';

      return () => {};
    }

    setEmpty(false);
    setLoading(true);
    const searchParam = makeSearchParam(input);
    if (!searchParam) {
      setPeople([]);
      setEmpty(true);
      setLoading(false);

      return () => {};
    }

    const params = new URLSearchParams({
      search: searchParam.value,
      limit: String(limit),
      searchType: searchParam.type,
    });
    const url = `/people2?${params.toString()}`;
    const currentApi = apiRef.current;
    const currentAuth = authRef.current;

    void apiInvoke<ApiResponse<PersonDocument[]>>(
      currentApi,
      url,
      currentAuth,
      {
        method: 'GET',
      },
    )
      .then((res) => {
        const items = Array.isArray(res?.data) ? res.data : [];
        const results = items.map(personDocumentToStorePerson);
        if (last.current === input) {
          setPeople(results);
          setEmpty(!results.length);
        }

        return undefined;
      })
      .catch((e) => {
        console.error(e);
        if (last.current === input) {
          setPeople([]);
          setEmpty(true);
        }
      })
      .finally(() => {
        if (last.current === input) {
          setLoading(false);
        }
      });

    return () => {
      last.current = '';
    };
  }, [limit, input]);

  return { people, loading, empty };
}

const PCO_GROUPS_ACCESS_LIMIT = 5000;
const PCO_SERVICE_TEAMS_ACCESS_LIMIT = 1000;

/**
 * PCO groups from Mongo (for policy / computeAccess). Loaded once when authorized.
 */
export function usePcoGroupsForAccess(): {
  list: PcoGroupAccessShape[];
  loading: boolean;
} {
  const { authorized } = useAuth();
  const { api, auth } = useApiWithAuth();
  const [list, setList] = useState<PcoGroupAccessShape[]>([]);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!authorized || loaded.current) {
      return () => {};
    }

    loaded.current = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('limit', String(PCO_GROUPS_ACCESS_LIMIT));
    params.set('offset', '0');
    const url = `/pco/groups?${params.toString()}`;

    void apiInvoke<ApiResponse<Record<string, unknown>[]>>(api, url, auth, {
      method: 'GET',
    })
      .then((res) => {
        const items = Array.isArray(res?.data) ? res.data : [];
        setList(
          items.map((row) => {
            const norm = normalizePcoGroupLeaderRows(
              row as Record<string, unknown>,
            );
            return {
              pco_id: String(row.pco_id ?? ''),
              mhx_group_leaders: norm.mhx_group_leaders,
              mhx_assistant_group_leaders: norm.mhx_assistant_group_leaders,
              mhx_parent_id:
                row.mhx_parent_id === undefined || row.mhx_parent_id === ''
                  ? null
                  : String(row.mhx_parent_id),
              mhx_campus_pco_id:
                row.mhx_campus_pco_id === undefined ||
                row.mhx_campus_pco_id === ''
                  ? null
                  : String(row.mhx_campus_pco_id),
              pco_campus_ids: Array.isArray(row.pco_campus_ids)
                ? (row.pco_campus_ids as unknown[]).map((x) => String(x))
                : [],
            } satisfies PcoGroupAccessShape;
          }),
        );

        return undefined;
      })
      .catch((e) => {
        console.error(e);
        setList([]);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {};
  }, [authorized, api, auth]);

  return { list, loading };
}

/**
 * PCO Services teams from Mongo (for policy / computeAccess). Loaded once when
 * authorized. Services currently caps this endpoint at 1,000 rows.
 */
export function usePcoServiceTeamsForAccess(): {
  list: PcoServiceTeamAccessShape[];
  loading: boolean;
} {
  const { authorized } = useAuth();
  const { api, auth } = useApiWithAuth();
  const [list, setList] = useState<PcoServiceTeamAccessShape[]>([]);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!authorized || loaded.current) {
      return () => {};
    }

    loaded.current = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('limit', String(PCO_SERVICE_TEAMS_ACCESS_LIMIT));
    params.set('offset', '0');
    const url = `/pco/services/teams?${params.toString()}`;

    void Promise.all([
      apiInvoke<ApiResponse<Record<string, unknown>[]>>(api, url, auth, {
        method: 'GET',
      }),
      apiInvoke<ApiResponse<Record<string, unknown>[]>>(
        api,
        '/pco/services/service-types',
        auth,
        { method: 'GET' },
      ),
    ])
      .then(([teamsResponse, serviceTypesResponse]) => {
        const items = Array.isArray(teamsResponse?.data)
          ? teamsResponse.data
          : [];
        const serviceTypes = Array.isArray(serviceTypesResponse?.data)
          ? serviceTypesResponse.data
          : [];
        const normalizeCampusIds = (value: unknown): string[] | undefined =>
          Array.isArray(value)
            ? value
                .filter((id): id is string => typeof id === 'string')
                .map((id) => id.trim())
                .filter(Boolean)
            : undefined;
        const campusMappingByServiceType = new Map(
          serviceTypes.map((row) => [
            String(row.pco_id ?? ''),
            {
              mhx: normalizeCampusIds(row.mhx_campus_ids),
              pco: normalizeCampusIds(row.pco_campus_ids) ?? [],
              mhxAuto: normalizeCampusIds(row.mhx_auto_campus_ids) ?? [],
            },
          ]),
        );
        setList(
          items.map((row) => {
            const serviceTypeId =
              row.pco_service_type_id === undefined ||
              row.pco_service_type_id === null ||
              row.pco_service_type_id === ''
                ? null
                : String(row.pco_service_type_id);

            const campusMapping = serviceTypeId
              ? campusMappingByServiceType.get(serviceTypeId)
              : undefined;

            return {
              pco_id: String(row.pco_id ?? ''),
              pco_service_type_id: serviceTypeId,
              ...(campusMapping?.mhx !== undefined
                ? { mhx_campus_ids: campusMapping.mhx }
                : {}),
              pco_campus_ids: campusMapping?.pco ?? [],
              mhx_auto_campus_ids: campusMapping?.mhxAuto ?? [],
              mhx_team_leaders: Array.isArray(row.mhx_team_leaders)
                ? row.mhx_team_leaders
                    .map((leader) => {
                      if (!leader || typeof leader !== 'object') {
                        return null;
                      }

                      const item = leader as Record<string, unknown>;
                      const pcoPersonId = String(
                        item.pco_person_id ?? '',
                      ).trim();
                      const mhxUserId = String(item.mhx_user_id ?? '').trim();
                      if (!pcoPersonId) {
                        return null;
                      }

                      return {
                        pco_person_id: pcoPersonId,
                        ...(mhxUserId ? { mhx_user_id: mhxUserId } : {}),
                      };
                    })
                    .filter(
                      (
                        leader,
                      ): leader is {
                        pco_person_id: string;
                        mhx_user_id?: string;
                      } => Boolean(leader),
                    )
                : [],
            };
          }),
        );

        return undefined;
      })
      .catch((error) => {
        console.error(error);
        setList([]);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {};
  }, [authorized, api, auth]);

  return { list, loading };
}
